import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card.js';
import { Button } from '../../components/ui/button.js';
import { Badge } from '../../components/ui/badge.js';
import { Progress } from '../../components/ui/progress.js';
import { Camera, Video, Settings, AlertTriangle, CheckCircle, XCircle, Info, HelpCircle, RotateCcw, Play, Pause, Volume2, VolumeX, Maximize2, Minimize2, Fullscreen, FullscreenExit, AlertCircle, X } from 'lucide-react';
import { useSafety } from '../../contexts/SafetyContext.js';
import { useSubscription } from '../../contexts/SubscriptionContext.js';
import { formAnalysisService } from '../../services/ai/formAnalysis.js';
import { safetyMonitoringService } from '../../services/ai/safetyMonitoring.js';
import { detectPosture, analyzeMovementPattern, calculateJointAngles, loadPoseNet } from '../../utils/motionAnalysis.js';
import { CameraSetupGuide } from './CameraSetupGuide.js';
import { FormFeedback } from './FormFeedback.js';
import { SafetyAlerts } from './SafetyAlerts.js';

const FormGuidance = ({ exercise }) => {
  const { logFormCheck } = useSafety();
  const { subscription, loading: subscriptionLoading } = useSubscription();
  const [formAnalysis, setFormAnalysis] = useState(null);
  const [safetyData, setSafetyData] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showSetupGuide, setShowSetupGuide] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [analysisError, setAnalysisError] = useState(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const [isStreamActive, setIsStreamActive] = useState(false);
  const [videoStream, setVideoStream] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(30);
  const videoRef = useRef(null);
  const analysisInterval = useRef(null);
  const analysisTimeout = useRef(null);
  const countdownInterval = useRef(null);
  const videoStreamRef = useRef(null);
  const cleanupInProgress = useRef(false);

  // Check camera availability on component mount
  useEffect(() => {
    const checkCameraAvailability = async () => {
      try {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          setCameraError('Camera not supported in this browser. Please use Chrome, Edge, or Firefox.');
          return;
        }

        // Try to enumerate devices to check if camera exists
        const devices = await navigator.mediaDevices.enumerateDevices();
        const hasVideoInput = devices.some(device => device.kind === 'videoinput');
        
        if (!hasVideoInput) {
          setCameraError('No camera detected. Please connect a camera to use form analysis.');
          return;
        }

        // Check permission status
        try {
          const permissionStatus = await navigator.permissions.query({ name: 'camera' });
          console.log('Camera permission status:', permissionStatus.state);
          
          if (permissionStatus.state === 'denied') {
            setCameraError('Camera permission denied. Please enable camera access:\n\n' +
                          '• Chrome/Edge: Click the lock icon (🔒) in the address bar\n' +
                          '• Firefox: Click the shield icon in the address bar\n' +
                          '• Or check your browser\'s privacy/security settings\n' +
                          '• Refresh this page after changing settings');
          } else if (permissionStatus.state === 'prompt') {
            // Permission hasn't been requested yet, this is normal
            console.log('Camera permission not yet requested');
          }
        } catch (permError) {
          console.log('Permission API not supported or failed:', permError.message);
          // Permission API not supported, continue
        }
      } catch (error) {
        console.log('Camera availability check failed:', error);
      }
    };

    checkCameraAvailability();
  }, []);

  // Cleanup function
  const cleanupFunction = () => {
    if (cleanupInProgress.current) return;
    cleanupInProgress.current = true;

    try {
      if (analysisInterval.current) {
        clearInterval(analysisInterval.current);
        analysisInterval.current = null;
      }
      if (analysisTimeout.current) {
        clearTimeout(analysisTimeout.current);
        analysisTimeout.current = null;
      }
      if (countdownInterval.current) {
        clearInterval(countdownInterval.current);
        countdownInterval.current = null;
      }
      if (videoStreamRef.current) {
        console.log('Stopping video stream...');
        videoStreamRef.current.getTracks().forEach(track => {
          console.log('Stopping track:', track.kind, track.label);
          track.stop();
        });
        videoStreamRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      setIsAnalyzing(false);
      setIsInitializing(false);
      setIsStreamActive(false);
      setVideoStream(null); // Clear video stream state
      setTimeRemaining(30);
      setAnalysisError(null);
    } finally {
      cleanupInProgress.current = false;
    }
  };

  // Effect to handle component unmount
  useEffect(() => {
    return () => {
      cleanupFunction();
    };
  }, []);

  const startFormAnalysis = async () => {
    if (isAnalyzing || isInitializing || isStreamActive) return;
    
    // Clear any existing errors when trying again
    setCameraError(null);
    setAnalysisError(null);
    
    try {
      setIsInitializing(true);
      setCameraError(null);
      setAnalysisError(null);
      setTimeRemaining(30);
      
      // First, try to initialize TensorFlow.js and PoseNet
      console.log('Initializing AI models...');
      try {
        await loadPoseNet();
        console.log('AI models initialized successfully');
      } catch (error) {
        console.error('Failed to initialize AI models:', error);
        setAnalysisError(error.message);
        return;
      }
      
      // Clean up any existing stream first
      if (videoStreamRef.current) {
        console.log('Cleaning up existing stream...');
        videoStreamRef.current.getTracks().forEach(track => track.stop());
        videoStreamRef.current = null;
      }
      
      console.log('Requesting camera access...');
      console.log('Current URL:', window.location.href);
      console.log('Is HTTPS:', window.location.protocol === 'https:');
      
      // Check current permission status first
      try {
        const permissionStatus = await navigator.permissions.query({ name: 'camera' });
        console.log('Camera permission status:', permissionStatus.state);
        
        if (permissionStatus.state === 'denied') {
          throw new Error('Camera permission is denied. Please enable camera access in your browser settings and refresh the page.');
        }
      } catch (permError) {
        console.log('Could not check permission status:', permError.message);
        // Continue anyway as permission API might not be supported
      }
      
      // List available devices first
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        console.log('Available video devices:', videoDevices.map(d => ({ id: d.deviceId, label: d.label })));
      } catch (deviceError) {
        console.log('Could not enumerate devices:', deviceError.message);
      }
      
      // Try with minimal constraints first
      let stream;
      try {
        console.log('Attempting camera access with minimal constraints...');
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user' }, // Minimal constraints
          audio: false
        });
        console.log('Camera access successful with minimal constraints');
      } catch (minimalError) {
        console.log('Minimal constraints failed:', minimalError.name, minimalError.message);
        
        try {
          console.log('Attempting camera access with basic constraints...');
          stream = await navigator.mediaDevices.getUserMedia({
            video: true, // Most basic constraint
            audio: false
          });
          console.log('Camera access successful with basic constraints');
        } catch (basicError) {
          console.log('Basic constraints failed:', basicError.name, basicError.message);
          
          try {
            console.log('Attempting camera access with specific constraints...');
            stream = await navigator.mediaDevices.getUserMedia({
              video: {
                width: { ideal: 640 },
                height: { ideal: 480 },
                facingMode: 'user',
                frameRate: { ideal: 30 }
              },
              audio: false
            });
            console.log('Camera access successful with specific constraints');
          } catch (specificError) {
            console.log('All camera access attempts failed');
            throw specificError; // Throw the last error
          }
        }
      }
      
      console.log('Camera access granted:', stream);
      videoStreamRef.current = stream;
      
      // Check if we actually got video tracks
      const videoTracks = stream.getVideoTracks();
      if (videoTracks.length === 0) {
        throw new Error('No video tracks found in the stream');
      }
      
      console.log('Video track details:', {
        label: videoTracks[0].label,
        enabled: videoTracks[0].enabled,
        readyState: videoTracks[0].readyState,
        settings: videoTracks[0].getSettings()
      });
      
      if (!videoRef.current) {
        throw new Error('Video element not found');
      }
      
      console.log('Setting video stream to video element');
      videoRef.current.srcObject = stream;
      
      // Wait for video to be ready
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Timeout waiting for video to be ready'));
        }, 5000);

        const checkReady = () => {
          if (videoRef.current.readyState >= 2) {
            clearTimeout(timeout);
            resolve();
          }
        };

        videoRef.current.onloadedmetadata = () => {
          console.log('Video metadata loaded');
          checkReady();
        };

        videoRef.current.oncanplay = () => {
          console.log('Video can play');
          checkReady();
        };

        videoRef.current.onerror = (error) => {
          clearTimeout(timeout);
          reject(new Error('Video element error: ' + error.message));
        };

        // Check if already ready
        checkReady();
      });

      // Add comprehensive video state debugging
      videoRef.current.onloadeddata = () => {
        console.log('Video data loaded:', {
          width: videoRef.current.videoWidth,
          height: videoRef.current.videoHeight,
          readyState: videoRef.current.readyState,
          srcObject: !!videoRef.current.srcObject,
          stream: !!stream,
          streamActive: stream.active,
          tracks: stream.getTracks().map(track => ({
            kind: track.kind,
            enabled: track.enabled,
            readyState: track.readyState,
            settings: track.getSettings()
          }))
        });
      };

      // Force a play attempt with error handling
      try {
        console.log('Attempting to play video...');
        await videoRef.current.play();
        console.log('Video playback started successfully');
        setIsStreamActive(true);
        setVideoStream(stream); // Set the video stream state
        setIsAnalyzing(true);
        
        // Set up countdown timer
        countdownInterval.current = setInterval(() => {
          setTimeRemaining(prev => {
            if (prev <= 1) {
              clearInterval(countdownInterval.current);
              stopAnalysis();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);

        // Set up analysis interval
        analysisInterval.current = setInterval(async () => {
          try {
            if (!videoRef.current || !videoRef.current.srcObject) {
              throw new Error('Video element not found or no stream');
            }

            const realTimeData = {
              postureData: await detectPosture(videoRef.current),
              movementData: await analyzeMovementPattern(videoRef.current),
              jointAngles: await calculateJointAngles(videoRef.current)
            };

            const feedback = await formAnalysisService.analyzeForm(exercise.id, realTimeData);
            setFormAnalysis(feedback);

            // Monitor safety
            const safety = await safetyMonitoringService.monitorWorkout({
              exerciseId: exercise.id,
              formAnalysis: feedback
            });
            setSafetyData(safety);

            // Log form check
            logFormCheck({
              exerciseId: exercise.id,
              timestamp: new Date(),
              passed: feedback.immediate.length === 0,
              feedback: feedback
            });
          } catch (error) {
            console.error('Error during form analysis:', error);
            setAnalysisError(error.message || 'Error analyzing form. Please try again.');
            stopAnalysis();
          }
        }, 1000);

        // Set timeout to stop analysis after 30 seconds
        analysisTimeout.current = setTimeout(() => {
          stopAnalysis();
        }, 30000);

      } catch (error) {
        console.error('Error playing video:', error);
        throw new Error('Failed to start video playback: ' + error.message);
      }
    } catch (error) {
      console.error('Error starting form analysis:', error);
      console.error('Error details:', {
        name: error.name,
        message: error.message,
        constraint: error.constraint,
        stack: error.stack
      });
      
      let errorMessage = 'Camera access is required for form analysis.';
      
      if (error.name === 'NotAllowedError') {
        if (error.message.includes('Permission denied by system')) {
          errorMessage = 'Camera access blocked by system security. This is a system-level restriction:\n\n' +
                        '• Your browser allows camera access, but your operating system is blocking it\n' +
                        '• Check your OS camera privacy settings (Windows/Mac/Linux)\n' +
                        '• Make sure your browser is allowed to access the camera in system settings\n' +
                        '• Try using a different browser (Chrome, Edge, Firefox)\n' +
                        '• Restart your browser and try again\n' +
                        '• Check if your antivirus/firewall is blocking camera access\n\n' +
                        'System Error: ' + error.message;
        } else {
          errorMessage = 'Camera permission denied. Even though your browser settings show "Allow", there might be a conflict:\n\n' +
                        '• Try clicking "Try Again" below to re-request permission\n' +
                        '• Check if another tab/application is using the camera\n' +
                        '• Try refreshing the page completely\n' +
                        '• If using Chrome, try incognito mode\n' +
                        '• Check your browser\'s site-specific camera settings\n\n' +
                        'Error details: ' + error.message;
        }
      } else if (error.name === 'NotFoundError') {
        errorMessage = 'No camera found. Please:\n\n' +
                      '• Connect a camera to your device\n' +
                      '• Ensure your camera is not being used by another application\n' +
                      '• Try refreshing the page\n\n' +
                      'Error details: ' + error.message;
      } else if (error.name === 'NotReadableError') {
        errorMessage = 'Camera is already in use. Please:\n\n' +
                      '• Close other applications using the camera\n' +
                      '• Try refreshing this page\n' +
                      '• Restart your browser if the issue persists\n\n' +
                      'Error details: ' + error.message;
      } else if (error.name === 'OverconstrainedError') {
        errorMessage = 'Camera constraints not supported. Please:\n\n' +
                      '• Try clicking "Try Again" below\n' +
                      '• Your camera might not support the required settings\n' +
                      '• Try using a different browser\n\n' +
                      'Error details: ' + error.message;
      } else if (error.message.includes('permission is denied')) {
        errorMessage = error.message;
      } else {
        errorMessage = 'Failed to access camera: ' + error.message + '\n\n' +
                      'Please try:\n' +
                      '• Clicking "Try Again" below\n' +
                      '• Refreshing the page\n' +
                      '• Using a different browser\n' +
                      '• Checking your camera connection';
      }
      
      setCameraError(errorMessage);
      cleanupFunction();
    } finally {
      setIsInitializing(false);
    }
  };

  const stopAnalysis = () => {
    cleanupFunction();
  };

  // Don't show upgrade prompts while subscription is loading
  if (subscriptionLoading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-3"></div>
          <div className="h-3 bg-gray-200 rounded w-2/3 mb-4"></div>
          <div className="h-20 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!subscription?.tier || subscription.tier === 'free') {
    return (
      <div className="text-center p-8">
        <AlertCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
        <h2 className="text-xl font-bold text-gray-800 mb-2">Upgrade Required</h2>
        <p className="text-gray-600">
          Upgrade to Premium to access real-time form analysis and personalized feedback.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Exercise Card */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-xl font-semibold mb-4">{exercise.name}</h3>
        <p className="text-gray-600 mb-6">{exercise.description}</p>
        
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
          <button
            onClick={() => setShowSetupGuide(true)}
            className="text-blue-600 hover:text-blue-700 font-medium flex items-center"
          >
            <Info className="mr-2 h-5 w-5" />
            View Setup Instructions
          </button>
          
          <button
            onClick={isAnalyzing ? stopAnalysis : startFormAnalysis}
            disabled={isInitializing}
            className={`${
              isAnalyzing 
                ? 'bg-red-600 hover:bg-red-700' 
                : 'bg-blue-600 hover:bg-blue-700'
            } text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center ${
              isInitializing ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <Play className={`mr-2 h-5 w-5 ${isAnalyzing ? 'rotate-90' : ''}`} />
            {isAnalyzing ? 'Stop Analysis' : 'Start Analysis'}
          </button>
        </div>
      </div>

      {/* Setup Guide Modal */}
      {showSetupGuide && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto relative">
            <button
              onClick={() => setShowSetupGuide(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              type="button"
            >
              <X className="h-6 w-6" />
            </button>
            <div className="p-6">
              <CameraSetupGuide />
            </div>
          </div>
        </div>
      )}

      {/* Error Messages */}
      {(cameraError || analysisError) && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <div className="flex items-start">
            <AlertCircle className="text-red-500 mt-1 mr-2 flex-shrink-0" />
            <div>
              <p className="text-red-700 whitespace-pre-line">{cameraError || analysisError}</p>
              <button
                onClick={startFormAnalysis}
                className="mt-2 text-red-600 hover:text-red-700 font-medium"
                type="button"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Video Preview */}
      <div className="relative w-full aspect-video bg-gray-100 rounded-lg overflow-hidden">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="absolute inset-0 w-full h-full object-cover"
          style={{ display: isStreamActive ? 'block' : 'none' }}
        />
        {!isStreamActive && (
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="text-gray-500">Camera preview will appear here</p>
          </div>
        )}
        {isAnalyzing && isStreamActive && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="text-white text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
              <p>Analyzing form...</p>
              <p className="text-2xl font-bold mt-2">{timeRemaining}s</p>
            </div>
          </div>
        )}
        {isStreamActive && !isAnalyzing && (
          <div className="absolute top-4 left-4 bg-green-500 text-white px-3 py-1 rounded-full text-sm">
            <Camera className="inline w-4 h-4 mr-1" />
            Camera Active
          </div>
        )}
      </div>

      {/* Exercise Instructions */}
      <div className="mb-6">
        <h4 className="font-semibold mb-2">Current Exercise: {exercise.name}</h4>
        <p className="text-gray-600">{exercise.description}</p>
      </div>

      {/* Camera Access Info */}
      {!cameraError && !isAnalyzing && !isStreamActive && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-semibold text-blue-800 mb-2">📹 Camera Access Required</h4>
          <p className="text-blue-700 text-sm mb-2">
            When you click "Start Analysis", your browser will ask for camera permission.
          </p>
          <p className="text-blue-700 text-sm">
            <strong>If you get "Permission denied by system":</strong> Check your operating system's camera privacy settings:
          </p>
          <ul className="text-blue-700 text-sm mt-2 ml-4 list-disc">
            <li><strong>Windows:</strong> Settings → Privacy → Camera → Allow apps to access your camera</li>
            <li><strong>Mac:</strong> System Preferences → Security & Privacy → Camera</li>
            <li><strong>Linux:</strong> Check your desktop environment's privacy settings</li>
          </ul>
        </div>
      )}

      {/* Ready to Start? */}
      {!isAnalyzing && !isStreamActive && (
        <div className="mb-6">
          <h4 className="font-semibold mb-2">Ready to Start?</h4>
          <ol className="list-decimal list-inside text-gray-600 space-y-2">
            <li>Position yourself in front of the camera</li>
            <li>Ensure good lighting</li>
            <li>Stand about 6 feet from the camera</li>
            <li>Click "Start Analysis" when ready</li>
          </ol>
        </div>
      )}

      {/* Analysis Results */}
      {formAnalysis && (
        <div className="mt-6">
          <FormFeedback feedback={formAnalysis} />
        </div>
      )}

      {/* Safety Alerts */}
      {safetyData?.alerts && (
        <div className="mt-4">
          <SafetyAlerts alerts={safetyData.alerts} />
        </div>
      )}
    </div>
  );
};

export default FormGuidance;